import React, { useState, useEffect } from 'react';
import { lazyPage } from './utils/lazyPage';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './providers/authProvider';
import { useUser } from './providers/userProvider';
import { Login } from './pages/login/page';
import { ForgotPassword } from './pages/forgotPassword/page';
import { CheckEmail } from './pages/checkEmail/page';
import { ResetPassword } from './pages/resetPassword/page';
import { SidebarLayout } from './components/ui/sidebar/sidebarLayout';
import { Loader } from 'lucide-react';
import { NoAccess } from './pages/no-access/page';
import { ReleaseGate } from './components/releaseGate';

// Signed-in pages load on demand so the login page and each route only
// download the code they use (#296). Login and the other signed-out pages
// stay in the main bundle so a cold visit needs one request.
const Home = lazyPage(() => import('./pages/home/page'), 'Home');
const Locations = lazyPage(() => import('./pages/locations/page'), 'Locations');
const Carts = lazyPage(() => import('./pages/carts/page'), 'Carts');
const CartDetail = lazyPage(() => import('./pages/cart-detail/page'), 'CartDetail');
const Users = lazyPage(() => import('./pages/users/page'), 'Users');
const Customers = lazyPage(() => import('./pages/customers/page'), 'Customers');
const ToggleRegistration = lazyPage(() => import('./pages/toggleRegistration/page'), 'ToggleRegistration');
const Dashboard = lazyPage(() => import('./pages/dashboard/page'), 'Dashboard');
const CustomerDashboard = lazyPage(() => import('./pages/customer-dashboard/page'), 'CustomerDashboard');
const PortalPreview = lazyPage(() => import('./pages/portal-preview/page'), 'PortalPreview');
const CustomerDetail = lazyPage(() => import('./pages/customer-detail/page'), 'CustomerDetail');
const CustomerUsers = lazyPage(() => import('./pages/customer-users/page'), 'CustomerUsers');
const CustomerSettings = lazyPage(() => import('./pages/customer-settings/page'), 'CustomerSettings');
const GeofencePage = lazyPage(() => import('./pages/geofence/page'), 'GeofencePage');
const Engagement = lazyPage(() => import('./pages/engagement/page'), 'Engagement');
const CartSignals = lazyPage(() => import('./pages/cart-signals/page'), 'CartSignals');

const Root = () => {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? '/home' : '/login'} replace />;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, refreshTokenIfNeeded } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await refreshTokenIfNeeded();
      } finally {
        setIsChecking(false);
      }
    };
    checkAuth();
  }, [refreshTokenIfNeeded]);

  if (isChecking) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Remember where the user was headed (e.g. a /cart/:id/toggle link) so the
    // login page can send them back there after signing in.
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  return children;
};

const UserTypeSwitch = ({
  internal,
  customer,
}: {
  internal: React.ReactNode;
  customer: React.ReactNode;
}) => {
  const { user, isCustomerUser, hasNoPortalAccess } = useUser();

  // Wait for user data to load
  if (!user) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    );
  }

  // Unclassified users get a generic landing page rather than either portal.
  if (hasNoPortalAccess) {
    return <NoAccess />;
  }

  return isCustomerUser ? customer : internal;
};

export const router = createBrowserRouter([
  {
    path: '/test',
    element: (
      <SidebarLayout>
        <div>Test</div>
      </SidebarLayout>
    ),
  },
  {
    path: '/',
    element: <Root />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/check-email',
    element: <CheckEmail />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
  {
    path: '/cart/:modemId/toggle',
    element: (
      <ProtectedRoute>
        <ToggleRegistration />
      </ProtectedRoute>
    ),
  },

  {
    path: '/customers/manage',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <Customers />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <CustomerDetail />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId/locations',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <Locations />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId/locations/:locationId/carts',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <Carts />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId/locations/:locationId/carts/:cartExternalId',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <CartDetail />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId/locations/:locationId/geofence',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <GeofencePage />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId/users',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <CustomerUsers />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers/:customerId/settings',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <CustomerSettings />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/customers',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <Home />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <UserTypeSwitch
          internal={
            <SidebarLayout>
              <Dashboard />
            </SidebarLayout>
          }
          customer={<CustomerDashboard />}
        />
      </ProtectedRoute>
    ),
  },
  {
    path: '/users',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <Users />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/engagement',
    element: (
      <ProtectedRoute>
        <SidebarLayout>
          <Engagement />
        </SidebarLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/cart-signals',
    element: (
      <ProtectedRoute>
        <ReleaseGate feature="Cart Signals">
          <SidebarLayout>
            <CartSignals />
          </SidebarLayout>
        </ReleaseGate>
      </ProtectedRoute>
    ),
  },
  // Portal Preview (for internal users to preview customer experience)
  {
    path: '/portal-preview/:customerId',
    element: (
      <ProtectedRoute>
        <PortalPreview />
      </ProtectedRoute>
    ),
  },
]);
